/**
 * Scenario scoring — the external mission scorer's contract (Port of Long
 * Beach benchmark et al.).
 *
 * Three scoring vocabularies coexist in this package; pick the right one:
 *  - `Scorecard` (scorecard.ts)          — reusable weighted bundle of Judges
 *    attached to workflow Evaluations.
 *  - `TrialScorecard` (trial-scorecard.ts) — live sense-hub polling + metric
 *    lines a benchmark kit stamps onto persistent-agent threads.
 *  - `ScenarioScore` (this file)          — POST-HOC scoring of one externally
 *    simulated mission from its event log. The sim emits scenario-tagged
 *    events and NO scores (agreed contract); this scorer runs outside the sim
 *    and must be usable by every participating agent stack, cortex or not.
 *
 * Versioning: `schemaVersion` is bumped only on breaking changes; additions
 * are additive-only. v1 deliberately does NOT define how a score embeds into
 * BenchmarkRun — that decision is deferred until the driver protocol for the
 * external sim is settled.
 */
import { z } from 'zod';
import { OrchestrationTopologySchema } from './orchestration-topology.js';
// ─── Normalized scenario events (scorer input) ─────────────────────────────
/**
 * Event kinds the scorer understands. Adapters map raw sim/Formant records
 * into these; anything unmappable becomes `other` (preserved + warned about,
 * ignored by metrics) so an unknown taxonomy never breaks scoring.
 */
export const ScenarioEventKindSchema = z.enum([
    'scenario_started',
    'event_spawned',
    'event_cleared',
    'scenario_completed',
    'world_edited',
    'scenario_reset',
    'other',
]);
/**
 * Which clock the log's `tMs` values are in. Manifest spawn schedules are
 * scenario-relative; mixing domains without declaring them makes per-event
 * time-to-clear silently wrong.
 */
export const ScenarioClockDomainSchema = z.enum(['sim', 'wall']);
/** One normalized event in a scenario log. */
export const ScenarioEventSchema = z.object({
    scenarioId: z.string().min(1),
    kind: ScenarioEventKindSchema,
    /** Stable per-mission-event id — required to pair spawn/clear. */
    eventId: z.string().min(1).optional(),
    /** Timestamp in ms within the log's declared clock domain. */
    tMs: z.number().finite(),
    /** Normalized robot identifier ("cleared by robot 2") when known. */
    robotId: z.string().min(1).optional(),
    /** Original record, passed through untouched for audit/debug. */
    raw: z.unknown().optional(),
});
/** A full scenario log: events plus their declared clock domain. */
export const ScenarioEventLogSchema = z.object({
    clock: ScenarioClockDomainSchema,
    events: z.array(ScenarioEventSchema),
});
// ─── Run manifest (the join key across stacks) ──────────────────────────────
/**
 * Who ran the scenario, with what, when. Written per attempt (by hand during
 * the manual bake-off; by the benchmark driver once runs are automated).
 * This is what makes results from different agent stacks attributable and
 * lets cortex-side agent stats be joined by agent ids + time window (turn
 * rows carry no scenario linkage).
 */
export const ScenarioRunManifestSchema = z.object({
    scenarioId: z.string().min(1),
    /**
     * Which agent stack ran it — see knownStacks / stackDisplayName in
     * `benchmark-stack.ts` (e.g. "metaphysics-orchestration", "claude-code",
     * "manual"). Kept an open string so arbitrary harnesses can participate.
     */
    stack: z.string().min(1),
    /** Person responsible for the attempt. */
    operator: z.string().min(1).optional(),
    /**
     * Sim world/tenant the attempt ran in (worlds are namespaced per person —
     * "what world it is in" is the first thing a comparison must show).
     */
    world: z.string().min(1).optional(),
    /** cortex orchestration group the attempt's agents belonged to, if any. */
    orchestrationGroupId: z.string().min(1).optional(),
    /** Wall-clock attempt window (ISO datetimes; timezone offsets accepted). */
    window: z
        .object({
        startedAt: z.string().datetime({ offset: true }),
        endedAt: z.string().datetime({ offset: true }).optional(),
    })
        .optional(),
    /** Free-form profile description (model, memory, mcps, charter, …). */
    profile: z.record(z.string(), z.unknown()).default({}),
    /** cortex persistent-agent ids involved, when the stack is cortex. */
    persistentAgentIds: z.array(z.string().min(1)).default([]),
    /**
     * Fallback spawn schedule (scenario-relative ms) when the sim does not
     * emit event_spawned events. Cross-checked against emitted spawns.
     */
    spawnScheduleMs: z.record(z.string(), z.number().finite()).optional(),
    /** Expected number of mission events, when known from the scenario def. */
    eventsTotal: z.number().int().positive().optional(),
    /**
     * Inbound operator-authored chat/event messages delivered to the run's
     * agents during the run window (cortex-side interference lane). The sim's
     * own interference log sees only operator-PLANE actions (scene mutations);
     * chat nudges travel through cortex and are invisible to it, yet they
     * contaminate a scored run the same way. > 0 ⇒ the run was not zero-touch
     * and evaluators may discount it.
     */
    operatorMessageCount: z.number().int().nonnegative().optional(),
    /**
     * The orchestration topology this attempt ran with — how the stack organized
     * itself (§10). For cortex runs the benchmark module DERIVES this from
     * primitives and the manifest endpoint echoes it; for a non-cortex harness
     * (claude-code) the harness SELF-REPORTS it here. Absent = not captured.
     */
    topology: OrchestrationTopologySchema.optional(),
});
// ─── Agent stats (optional join, cortex stacks only) ────────────────────────
/** Aggregated persistent-agent turn stats for the attempt window. */
export const ScenarioAgentStatsSchema = z.object({
    usd: z.number().nonnegative().nullable(),
    turns: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    toolErrors: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
});
// ─── Anti-cheat ─────────────────────────────────────────────────────────────
/**
 * Primary signal: scenario-tagged world_edited / scenario_reset events from
 * the sim (uniform across stacks). Secondary: a scan of cortex tool-call rows
 * — explicitly best-effort (writes are fire-and-forget and only cover cortex
 * stacks), so it corroborates rather than proves.
 */
export const ScenarioAntiCheatSchema = z.object({
    simEvents: z.array(ScenarioEventSchema).default([]),
    cortexToolScan: z
        .object({
        flagged: z
            .array(z.object({ toolName: z.string(), count: z.number().int().positive() }))
            .default([]),
        coverage: z.literal('best-effort').default('best-effort'),
    })
        .optional(),
    /**
     * Operator-plane activity recorded by the sim during the run (`interfered`
     * flag + interference log in the run dataset). Uniform across stacks —
     * evaluators may discount an interfered run. Raw log preserved for audit.
     */
    interference: z
        .object({
        flagged: z.boolean(),
        entries: z.number().int().nonnegative(),
        log: z.unknown().optional(),
    })
        .optional(),
    /**
     * Forbidden-tool scan over the sim-recorded agent tool-call log (run
     * dataset). Unlike cortexToolScan this covers ANY stack — the sim records
     * every MCP call it served.
     */
    simToolScan: z
        .object({
        flagged: z
            .array(z.object({ toolName: z.string(), count: z.number().int().positive() }))
            .default([]),
        coverage: z.literal('sim-recorded').default('sim-recorded'),
    })
        .optional(),
});
// ─── Provenance (baseline identity) ─────────────────────────────────────────
/**
 * The comparability triple. Baselines pin to (scenario, version, sim commit);
 * scores from differing triples are not directly comparable (the sim repo's
 * CHANGELOG attributes score movement between sim versions).
 */
export const ScenarioProvenanceSchema = z.object({
    scenario: z.string().optional(),
    scenarioVersion: z.union([z.string(), z.number()]).optional(),
    simCommit: z.string().optional(),
});
// ─── Behavior (how the run was played, from the sim action log) ────────────
/**
 * Process metrics computed from the sim-recorded agent action log
 * (`agent_actions` in the run dataset) — the mechanism lane between config
 * (resolvedProfile/manifest) and outcome (ScenarioScore.outcome). Uniform
 * across stacks because the sim records every MCP call it served.
 *
 * Validated on the 2026-08-03/04 ten-trial campaign: expiries tracked blind
 * windows (>45 s gaps between event-information polls) almost perfectly for
 * the serial harness, while second-half activity tracked the platform's
 * scores — the arms fail as different species, and these fields are what
 * separates them.
 *
 * All fields are best-effort: heuristics are documented on the computing
 * functions in @cortex/scenario-scoring, and anything uncomputable is null —
 * never a thrown error.
 */
export const ScenarioBehaviorSchema = z.object({
    /** Event-information polls (list_events/get_scenario_status) per minute. */
    pollCadence: z.object({
        firstHalfPerMin: z.number().nonnegative().nullable(),
        secondHalfPerMin: z.number().nonnegative().nullable(),
        /** secondHalf/firstHalf; 1 = sustained attention, →0 = fade. Null when the first half had no polls. */
        decayRatio: z.number().nonnegative().nullable(),
    }),
    /** Longest stretch with no event-information poll, mission window included. */
    maxBlindWindowMs: z.number().nonnegative().nullable(),
    /** Gaps > 45 s between event-information polls (mission-relative ms). */
    blindWindows: z
        .array(z.object({
        startMs: z.number().nonnegative(),
        endMs: z.number().nonnegative(),
        durationMs: z.number().nonnegative(),
    }))
        .default([]),
    /** Events that spawned inside a blind window… */
    eventsSpawnedInBlindWindows: z.number().int().nonnegative(),
    /** …and how many of those were never resolved (the linkage stat). */
    blindWindowExpiries: z.number().int().nonnegative(),
    /** Spawn → first robot-directed command (resolved events, heuristic pairing). */
    dispatchLatency: z.object({
        medianMs: z.number().nonnegative().nullable(),
        p90Ms: z.number().nonnegative().nullable(),
        samples: z.number().int().nonnegative(),
    }),
    parallelism: z.object({
        distinctRobotsUsed: z.number().int().nonnegative(),
        /** Max distinct robots commanded within any rolling 60 s window. */
        maxRobotsInRolling60s: z.number().int().nonnegative(),
    }),
    /** reads = get_ / list_ prefixed calls; acts = everything else (incl. waits). */
    callCounts: z.object({
        total: z.number().int().nonnegative(),
        reads: z.number().int().nonnegative(),
        acts: z.number().int().nonnegative(),
    }),
    toolErrorCount: z.number().int().nonnegative(),
    /**
     * Photo/camera call attempts vs successes. Null when the sim's action log
     * carries no photo calls at all (a known sim logging gap — photos exist in
     * the dataset but their tool calls are absent from agent_actions).
     */
    photoRetries: z
        .object({
        attempts: z.number().int().nonnegative(),
        successes: z.number().int().nonnegative(),
    })
        .nullable(),
});
// ─── The scorecard (scorer output) ──────────────────────────────────────────
/**
 * Censored-data stance: with ~15 events some go uncleared, so time-to-clear
 * is reported as median/p90 over CLEARED events only, alongside clearRate and
 * unclearedCount — never a bare mean (an agent clearing 6 easy events fast
 * must not outrank one clearing 14).
 */
export const ScenarioTimeToClearSchema = z.object({
    medianMs: z.number().nonnegative().nullable(),
    p90Ms: z.number().nonnegative().nullable(),
    meanMs: z.number().nonnegative().nullable(),
    /** Cleared events whose spawn time was unknown (TTC not computable). */
    unknownSpawnCount: z.number().int().nonnegative(),
});
export const ScenarioRobotAttributionSchema = z.object({
    robotId: z.string(),
    cleared: z.number().int().nonnegative(),
});
export const ScenarioOutcomeSchema = z.object({
    /** Total mission events: emitted spawns, else manifest, else null. */
    eventsTotal: z.number().int().nonnegative().nullable(),
    eventsCleared: z.number().int().nonnegative(),
    /** eventsCleared / eventsTotal; null when eventsTotal unknown. */
    clearRate: z.number().min(0).max(1).nullable(),
    unclearedCount: z.number().int().nonnegative().nullable(),
    timeToClear: ScenarioTimeToClearSchema,
    /** True iff a scenario_completed event was observed. */
    completed: z.boolean(),
    /** scenario_started → scenario_completed (or last event) span. */
    scenarioDurationMs: z.number().nonnegative().nullable(),
    byRobot: z.array(ScenarioRobotAttributionSchema),
});
export const ScenarioScoreSchema = z.object({
    schemaVersion: z.literal(1),
    scenarioId: z.string().min(1),
    clock: ScenarioClockDomainSchema,
    manifest: ScenarioRunManifestSchema.nullable(),
    outcome: ScenarioOutcomeSchema,
    agentStats: ScenarioAgentStatsSchema.nullable(),
    antiCheat: ScenarioAntiCheatSchema,
    /** Baseline identity triple (scenario, version, sim commit), when known. */
    provenance: ScenarioProvenanceSchema.optional(),
    /**
     * Process metrics from the sim action log, when the input document carries
     * one (additive; absent for plain event-log inputs).
     */
    behavior: ScenarioBehaviorSchema.optional(),
    /** Machine-readable data-quality notes; never silently dropped. */
    warnings: z.array(z.string()),
    meta: z.record(z.string(), z.unknown()).default({}),
});
//# sourceMappingURL=scenario-score.js.map